// Scale-driven proficiency band chip — promoted from analyse on its second
// consumer (gradebook). Authored level colors win; slot palette (tokens) is
// the fallback; legacy canonical keys keep their historic label map.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  indexOfLabel,
  slotChipClasses,
  slotForIndex,
  type GradingScaleLevel,
  type PaletteVariant,
} from '@/components/ap/proficiency/proficiencyPalette';
import { cn } from '@/lib/cn';
import { useThemeStore } from '@/lib/store/themeStore';
import { blendHexOverToken } from '@/lib/tokens';

export interface ProficiencyBandChipProps {
  band: string | null | undefined;
  variant?: PaletteVariant;
  gradingScaleLevels?: ReadonlyArray<GradingScaleLevel>;
}

const SUMMARY_LABEL_KEYS: Record<string, string> = {
  below_basic: 'AnalyseV3Proficiency.below_basic',
  basic: 'AnalyseV3Proficiency.basic',
  proficient: 'AnalyseV3Proficiency.proficient',
  advanced: 'AnalyseV3Proficiency.advanced',
};

const STANDARDS_LABEL_KEYS: Record<string, string> = {
  below_basic: 'AnalyseV3Proficiency.below_basic',
  basic: 'AnalyseV3Proficiency.basic',
  near_mastery: 'AnalyseV3Proficiency.near_mastery',
  mastery: 'AnalyseV3Proficiency.mastery',
};

const LEGACY_BAND_SLOT: Record<string, string> = {
  below_basic: 'below-basic',
  basic: 'basic',
  proficient: 'proficient',
  advanced: 'advanced',
  near_mastery: 'near-mastery',
  mastery: 'mastery',
};

const chipBase =
  'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap';

export const ProficiencyBandChip = ({
  band,
  variant = 'summary',
  gradingScaleLevels,
}: ProficiencyBandChipProps) => {
  const { t } = useTranslation();
  // Theme flips re-resolve --background, so the authored wash must re-blend.
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);

  const dynamicIndex = indexOfLabel(band, gradingScaleLevels);
  const authoredColor =
    dynamicIndex >= 0 ? gradingScaleLevels?.[dynamicIndex]?.color?.trim() : undefined;
  // Blend against --background (not white) so authored colors stay legible in
  // dark/hc. JS sRGB lerp, not runtime color-mix — Safari < 16.2 can't parse
  // color-mix strings (browser floor, THEME.md law 10). Unparseable authored
  // values (non-hex) fall through to the slot palette.
  const authoredWash = useMemo(
    () => (authoredColor ? blendHexOverToken(authoredColor, 'background', 0.15) : null),
    [authoredColor, resolvedTheme] // eslint-disable-line react-hooks/exhaustive-deps -- resolvedTheme invalidates the token blend
  );

  if (!band) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (dynamicIndex >= 0) {
    if (authoredColor && authoredWash) {
      return (
        <span
          className={chipBase}
          style={{ backgroundColor: authoredWash, color: authoredColor }}
          data-band={band}
        >
          {band}
        </span>
      );
    }
    // Levels arrive descending (top band first); slot palette is lowest-first — invert.
    const totalLevels = gradingScaleLevels?.length ?? 0;
    const slotIndex = Math.max(0, totalLevels - 1 - dynamicIndex);
    return (
      <span
        className={cn(chipBase, slotChipClasses(slotForIndex(slotIndex, variant)))}
        data-band={band}
      >
        {band}
      </span>
    );
  }

  // Legacy canonical keys (or unknown strings) — historic label map + slot colors.
  const labelKeyMap = variant === 'standards' ? STANDARDS_LABEL_KEYS : SUMMARY_LABEL_KEYS;
  const labelKey = labelKeyMap[band];
  const legacySlot = LEGACY_BAND_SLOT[band];

  return (
    <span
      className={cn(
        chipBase,
        legacySlot ? slotChipClasses(legacySlot) : 'bg-muted text-foreground'
      )}
      data-band={band}
    >
      {labelKey ? t(labelKey) : band}
    </span>
  );
};
